---
title: Health and troubleshooting
summary: Check the health endpoint, read the logs, and resolve the most common deployment issues.
visibility: authenticated
audience: operator
section: Operating
order: 126
last_verified: 2026-06-19
owner: docs
---

# Health and troubleshooting

## Health endpoint

The backend exposes `GET /health`. It returns an overall `status` of **healthy** or
**degraded** along with the status of its dependencies — the **database** and the configured
**RAG server**. If a dependency is down, the overall status goes to `degraded` and the failing
service is named, which makes it a good liveness/readiness probe.

## Logs

The backend logs through structured logging — JSON in production (easy to ship to a log
aggregator) and human-readable console output in development. Look here first when a request
fails.

## Common issues

- **Backend won't start: missing `SECRET_KEY`.** Required in production — set it (see
  [Configuration](/help/operator/configuration)).
- **Startup error about a feature dependency.** `ENABLED_FEATURES` enabled a feature without
  its dependencies; dependencies are validated, not auto-added. Include the whole chain (see
  [Feature profiles](/help/reference/feature-profiles)).
- **Answers come back generic, with no sources.** The persona may point at the wrong (or
  empty) graph, or the RAG server is unreachable — check `/health`'s RAG status and the
  persona's Graph ID ([RAG routing](/help/operator/rag-routing)).
- **A feature's pages are missing in the app.** It isn't enabled for this deployment — confirm
  via the feature profile and `ENABLED_FEATURES`.
- **A persona's tool fails to authenticate.** Its required credential set isn't wired — see
  [Manage credentials](/help/how-to/manage-credentials).

## See also

- [Deploy the app](/help/operator/deploy)
- [Configuration](/help/operator/configuration)
